import React, { useState } from "react";

import {
  Box,
  Chip,
  Grid,
  Paper,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Link } from "react-router-dom";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import DownloadIcon from "@mui/icons-material/Download";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReactPlayer from "react-player";

import { useCanEdit } from "../../../../hooks/useCanEdit";
import { Video as VideoType } from "../../types";

interface Props {
  video: VideoType;
  episodeId: string;
  /** Denser variant used in the Episode preview (3-up grid, smaller type). */
  compact?: boolean;
}

const VideoCard = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: "12px",
  border: `1px solid ${theme.palette.divider}`,
  overflow: "hidden",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  cursor: "pointer",
  position: "relative",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  boxShadow: "none",

  "&:hover": {
    borderColor: "#2563eb",
    boxShadow: "0 4px 16px rgba(37,99,235,0.10)",
  },

  [theme.breakpoints.down("sm")]: {
    "&:hover": {
      borderColor: theme.palette.divider,
      boxShadow: "none",
    },
  },

  "&.generating": {
    cursor: "not-allowed",
    opacity: 0.7,
    "&:hover": {
      borderColor: theme.palette.divider,
      boxShadow: "none",
    },
  },
}));

const VideoContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  aspectRatio: "16/9",
  backgroundColor: theme.palette.grey[900],
  overflow: "hidden",
  borderRadius: "12px 12px 0 0",
}));

const OverlayButton = styled(Box)(() => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "rgba(0, 0, 0, 0.65)",
  color: "white",
  borderRadius: "50%",
  width: 52,
  height: 52,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "background-color 0.2s ease",
  zIndex: 2,

  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.85)",
  },
}));

const GeneratingOverlay = styled(Box)(() => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.78)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 3,
  color: "white",
}));

export default function Video({ video, episodeId, compact = false }: Props) {
  const [isHovered, setIsHovered] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const canEdit = useCanEdit();

  const isGenerating = video.status === "generating" || !video.url;

  const handleMouseEnter = () => {
    if (!isMobile && !isGenerating) {
      setIsHovered(true);
      setIsPlaying(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile && !isGenerating) {
      setIsHovered(false);
      setIsPlaying(false);
    }
  };

  const handleTouchStart = () => {
    if (isMobile && !isGenerating) {
      setIsHovered(true);
      setIsPlaying(true);
    }
  };

  const handleTouchEnd = () => {
    if (isMobile && !isGenerating) {
      setTimeout(() => {
        setIsHovered(false);
        setIsPlaying(false);
      }, 2000);
    }
  };

  const cardContent = (
    <VideoCard
      className={isGenerating ? "generating" : undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      elevation={0}
      sx={compact ? { borderRadius: "10px" } : undefined}
    >
      <VideoContainer
        sx={
          compact
            ? { aspectRatio: "16 / 10", borderRadius: "10px 10px 0 0" }
            : undefined
        }
      >
        {isGenerating ? (
          <GeneratingOverlay>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, textAlign: "center" }}
            >
              Generating Video...
            </Typography>
            <Typography
              variant="caption"
              sx={{ textAlign: "center", opacity: 0.75, mt: 0.5 }}
            >
              This may take a few minutes
            </Typography>
          </GeneratingOverlay>
        ) : (
          <>
            <ReactPlayer
              url={video.url}
              width="100%"
              height="100%"
              playing={isPlaying}
              muted
              loop
              playsinline
            />

            {!isPlaying && (
              <OverlayButton>
                <PlayArrowIcon sx={{ fontSize: 22 }} />
              </OverlayButton>
            )}

            {isHovered && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  backgroundColor: "rgba(0,0,0,0.55)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "#2563eb",
                    color: "white",
                    borderRadius: "8px",
                    px: 2,
                    py: 0.875,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {canEdit ? (
                    <ContentCutIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <DownloadIcon sx={{ fontSize: 18 }} />
                  )}
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    fontSize="0.875rem"
                  >
                    {canEdit ? "Edit / Export" : "Export"}
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        )}
      </VideoContainer>

      {/* Card info */}
      <Box sx={{ p: compact ? 1 : 2, flexGrow: 1 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: compact ? "0.74rem" : "0.9rem",
            color: isGenerating ? "text.disabled" : "text.primary",
            lineHeight: compact ? 1.25 : 1.4,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            mb: compact ? 0.5 : 1,
          }}
        >
          {video.title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Chip
            label="Short Video"
            size="small"
            sx={{
              fontSize: compact ? "0.58rem" : "0.7rem",
              height: compact ? 18 : 22,
              backgroundColor: "#eff6ff",
              color: "#2563eb",
              border: "1px solid #bfdbfe",
              fontWeight: 600,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: compact ? "0.64rem" : "0.72rem",
            }}
          >
            {new Date(video.created_at).toLocaleDateString()}
          </Typography>
        </Box>
      </Box>
    </VideoCard>
  );

  return (
    <Grid
      item
      {...(compact ? { xs: 4 } : { xs: 12, sm: 6, md: 4, lg: 3, xl: 3 })}
    >
      {isGenerating ? (
        cardContent
      ) : (
        <Link
          to={`/episodes/${episodeId}/videos/${video.id}/edit`}
          style={{ textDecoration: "none", display: "block", height: "100%" }}
        >
          {cardContent}
        </Link>
      )}
    </Grid>
  );
}
